/**
 * Auto Assignment Settings Component
 * Displays and manages auto-assignment configuration for Tax Team processors
 * 
 * Features:
 * - Enable/disable auto-assignment
 * - State eligibility selection (all 50 US states)
 * - Daily target configuration
 * - WIP limit settings
 * - Stuck protection thresholds
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Settings, 
  MapPin, 
  Target, 
  AlertTriangle, 
  Clock,
  ToggleLeft,
  ToggleRight,
  Save,
  RefreshCw,
  CheckSquare,
  Square
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Switch } from '../ui/switch';
import { Badge } from '../ui/badge';

const API = process.env.REACT_APP_BACKEND_URL || '';

// US States list
const US_STATES = [
  { code: "AL", name: "Alabama" },
  { code: "AK", name: "Alaska" },
  { code: "AZ", name: "Arizona" },
  { code: "AR", name: "Arkansas" },
  { code: "CA", name: "California" },
  { code: "CO", name: "Colorado" },
  { code: "CT", name: "Connecticut" },
  { code: "DE", name: "Delaware" },
  { code: "FL", name: "Florida" },
  { code: "GA", name: "Georgia" },
  { code: "HI", name: "Hawaii" },
  { code: "ID", name: "Idaho" },
  { code: "IL", name: "Illinois" },
  { code: "IN", name: "Indiana" },
  { code: "IA", name: "Iowa" },
  { code: "KS", name: "Kansas" },
  { code: "KY", name: "Kentucky" },
  { code: "LA", name: "Louisiana" },
  { code: "ME", name: "Maine" },
  { code: "MD", name: "Maryland" },
  { code: "MA", name: "Massachusetts" },
  { code: "MI", name: "Michigan" },
  { code: "MN", name: "Minnesota" },
  { code: "MS", name: "Mississippi" },
  { code: "MO", name: "Missouri" },
  { code: "MT", name: "Montana" },
  { code: "NE", name: "Nebraska" },
  { code: "NV", name: "Nevada" },
  { code: "NH", name: "New Hampshire" },
  { code: "NJ", name: "New Jersey" },
  { code: "NM", name: "New Mexico" },
  { code: "NY", name: "New York" },
  { code: "NC", name: "North Carolina" },
  { code: "ND", name: "North Dakota" },
  { code: "OH", name: "Ohio" },
  { code: "OK", name: "Oklahoma" },
  { code: "OR", name: "Oregon" },
  { code: "PA", name: "Pennsylvania" },
  { code: "RI", name: "Rhode Island" },
  { code: "SC", name: "South Carolina" },
  { code: "SD", name: "South Dakota" },
  { code: "TN", name: "Tennessee" },
  { code: "TX", name: "Texas" },
  { code: "UT", name: "Utah" },
  { code: "VT", name: "Vermont" },
  { code: "VA", name: "Virginia" },
  { code: "WA", name: "Washington" },
  { code: "WV", name: "West Virginia" },
  { code: "WI", name: "Wisconsin" },
  { code: "WY", name: "Wyoming" },
  { code: "DC", name: "District of Columbia" },
];

// Numeric field editing bounds — mirror the Input min/max attributes.
// Values are clamped on blur/save (NOT while typing) so admins can freely
// select, clear and retype numbers without the field snapping back to a
// default mid-edit (the old `parseInt(v) || fallback` onChange did that).
const NUMERIC_FIELDS = {
  daily_target: { min: 0, max: 999, fallback: 0 },
  wip_limit: { min: 1, max: 50, fallback: 8 },
  stuck_threshold_count: { min: 1, max: 50, fallback: 3 },
  stuck_threshold_hours: { min: 1, max: 168, fallback: 24 },
};

const clampNumeric = (key, raw) => {
  const { min, max, fallback } = NUMERIC_FIELDS[key];
  const n = parseInt(raw, 10);
  if (Number.isNaN(n)) return fallback;
  return Math.min(max, Math.max(min, n));
};

const AutoAssignmentSettings = ({ userId, userRole, userRoles, onUpdate, onSaveComplete }) => {
  const [settings, setSettings] = useState({
    auto_assign_enabled: false,
    eligible_states: [],
    // Phase A.5 — multi-dimensional eligibility (empty list = "all")
    eligible_client_codes: [],
    eligible_product_ids: [],
    daily_target: 0,
    wip_limit: 8,
    stuck_threshold_count: 3,
    stuck_threshold_hours: 24,
    assignment_lock_allowed: true
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  // Phase A.5 — catalogs for client + product dropdowns
  const [clientsCatalog, setClientsCatalog] = useState([]);
  const [productsCatalog, setProductsCatalog] = useState([]);
  const [clientFilter, setClientFilter] = useState('');
  const [productFilter, setProductFilter] = useState('');

  // Phase A.6 (Jun 6, 2026) — settings panel renders for both Tax (`processor`,
  // `tax_processor`) AND Search (`search_processor`) operators. The team name
  // is derived from the role and shown in the panel header. Engine routes the
  // user to the right team automatically via the role filter in the engine
  // and the workflow-type safety gate.
  const normalizedRole = (userRole || '').toLowerCase().replace(/\s+/g, '_').replace(/-/g, '_');
  const normalizeStr = (s) => (s || '').toLowerCase().replace(/\s+/g, '_').replace(/-/g, '_');
  const isTaxProcessor =
    ['processor', 'tax_processor'].includes(normalizedRole) ||
    (Array.isArray(userRoles) && userRoles.some(r =>
      typeof r === 'string'
        ? (r.toLowerCase().includes('processor') && !r.toLowerCase().includes('search'))
        : (r.name?.toLowerCase().includes('processor') && !r.name?.toLowerCase().includes('search'))
    ));
  const isSearchProcessor =
    normalizedRole === 'search_processor' ||
    (Array.isArray(userRoles) && userRoles.some(r =>
      typeof r === 'string'
        ? normalizeStr(r) === 'search_processor'
        : normalizeStr(r?.name) === 'search_processor'
    ));
  const isAutoAssignableProcessor = isTaxProcessor || isSearchProcessor;
  const userTeamLabel = isSearchProcessor ? 'Search Team' : (isTaxProcessor ? 'Tax Team' : 'Team');

  useEffect(() => {
    if (userId && isAutoAssignableProcessor) {
      fetchSettings();
    } else {
      setLoading(false);
    }
  }, [userId, isAutoAssignableProcessor]);

  // Phase A.5 — fetch catalogs once when the panel mounts for an auto-assignable processor
  useEffect(() => {
    if (isAutoAssignableProcessor) {
      loadCatalogs();
    }
  }, [isAutoAssignableProcessor]);

  const loadCatalogs = async () => {
    try {
      // Phase A.6 — scope products to the user's team so Tax processors
      // only see Tax products and Search processors only see Title products.
      const teamParam = isSearchProcessor ? 'search' : (isTaxProcessor ? 'tax' : null);
      const productsUrl = teamParam
        ? `${API}/api/auto-assignment/eligible-products?team=${teamParam}`
        : `${API}/api/auto-assignment/eligible-products`;
      const [clientsResp, productsResp] = await Promise.all([
        axios.get(`${API}/api/auto-assignment/eligible-clients`),
        axios.get(productsUrl),
      ]);
      setClientsCatalog(clientsResp.data?.clients || []);
      setProductsCatalog(productsResp.data?.products || []);
    } catch (err) {
      console.error('Error fetching eligibility catalogs:', err);
    }
  };

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/api/auto-assignment/users/${userId}/auto-assign-settings`);
      setSettings(response.data);
    } catch (err) {
      console.error('Error fetching auto-assign settings:', err);
      // Use defaults if not found
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      
      // Validation
      if (settings.auto_assign_enabled && settings.eligible_states.length === 0) {
        toast.error('Please select at least one eligible state when auto-assignment is enabled');
        return;
      }
      
      // Coerce numeric fields (they may hold raw strings mid-edit) and
      // clamp to valid ranges — numeric validation happens here and on
      // blur, never while the admin is typing.
      const payload = { ...settings };
      Object.keys(NUMERIC_FIELDS).forEach((key) => {
        payload[key] = clampNumeric(key, payload[key]);
      });

      await axios.patch(`${API}/api/auto-assignment/users/${userId}/auto-assign-settings`, payload);
      setSettings(payload);
      toast.success('Auto-assignment settings saved successfully');
      setHasChanges(false);
      if (onUpdate) onUpdate(payload);
      
      // Auto-close drawer after successful save (UX improvement)
      if (onSaveComplete) {
        // Small delay to let user see the success toast
        setTimeout(() => {
          onSaveComplete();
        }, 500);
      }
    } catch (err) {
      console.error('Error saving settings:', err);
      // AUTOASSIGN-SAVE-400: surface the backend validation message instead
      // of a generic failure. FastAPI returns `detail` as a string (400) or
      // an array of {loc,msg} objects (422 schema errors).
      const detail = err.response?.data?.detail;
      const message = typeof detail === 'string'
        ? detail
        : Array.isArray(detail)
          ? detail.map((d) => {
              const field = Array.isArray(d.loc) ? d.loc[d.loc.length - 1] : '';
              return field ? `${field}: ${d.msg}` : (d.msg || JSON.stringify(d));
            }).join('; ')
          : 'Failed to save settings';
      toast.error(message, { duration: 8000 });
    } finally {
      setSaving(false);
    }
  };

  const updateSetting = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
    setHasChanges(true);
  };

  const toggleState = (stateCode) => {
    const newStates = settings.eligible_states.includes(stateCode)
      ? settings.eligible_states.filter(s => s !== stateCode)
      : [...settings.eligible_states, stateCode];
    updateSetting('eligible_states', newStates);
  };

  const selectAllStates = () => {
    updateSetting('eligible_states', US_STATES.map(s => s.code));
  };

  const clearAllStates = () => {
    updateSetting('eligible_states', []);
  };

  // Phase A.5 — client + product toggle helpers
  const toggleClient = (code) => {
    const current = settings.eligible_client_codes || [];
    const next = current.includes(code)
      ? current.filter(c => c !== code)
      : [...current, code];
    updateSetting('eligible_client_codes', next);
  };

  const toggleProduct = (id) => {
    const current = settings.eligible_product_ids || [];
    const next = current.includes(id)
      ? current.filter(p => p !== id)
      : [...current, id];
    updateSetting('eligible_product_ids', next);
  };

  const clearClients = () => updateSetting('eligible_client_codes', []);
  const clearProducts = () => updateSetting('eligible_product_ids', []);

  // Don't show for users who aren't on an auto-assignable team
  if (!isAutoAssignableProcessor) {
    return null;
  }

  if (loading) {
    return (
      <Card className="auto-assign-settings">
        <CardContent className="p-6">
          <div className="flex items-center justify-center py-8">
            <RefreshCw className="h-6 w-6 animate-spin text-gray-400" />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="auto-assign-settings mt-6" data-testid="auto-assign-settings">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Settings className="h-5 w-5 text-blue-600" />
            <CardTitle className="text-lg">Auto Assignment Settings ({userTeamLabel})</CardTitle>
          </div>
          {hasChanges && (
            <Badge variant="outline" className="text-orange-600 border-orange-300">
              Unsaved changes
            </Badge>
          )}
        </div>
        <CardDescription>
          Configure automatic order assignment for this processor
        </CardDescription>
      </CardHeader>
      
      <CardContent className="space-y-6">
        {/* Enable/Disable Toggle — standardized row: label + description +
            explicit Enabled/Disabled status text + switch. Whole row is a
            click target. */}
        <div
          className="flex items-center justify-between p-4 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors"
          onClick={() => updateSetting('auto_assign_enabled', !settings.auto_assign_enabled)}
          data-testid="auto-assign-row"
        >
          <div className="flex items-center gap-3">
            {settings.auto_assign_enabled ? (
              <ToggleRight className="h-6 w-6 text-green-600" />
            ) : (
              <ToggleLeft className="h-6 w-6 text-gray-400" />
            )}
            <div>
              <Label className="text-base font-medium cursor-pointer">Auto Assignment</Label>
              <p className="text-sm text-gray-500">
                {settings.auto_assign_enabled 
                  ? 'Orders will be automatically assigned to this processor' 
                  : 'Manual assignment only'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
            <span
              className={`text-sm font-semibold ${settings.auto_assign_enabled ? 'text-green-600' : 'text-gray-400'}`}
              data-testid="auto-assign-status-text"
            >
              {settings.auto_assign_enabled ? 'Enabled' : 'Disabled'}
            </span>
            <Switch
              checked={!!settings.auto_assign_enabled}
              onCheckedChange={(checked) => updateSetting('auto_assign_enabled', checked)}
              data-testid="auto-assign-toggle"
            />
          </div>
        </div>

        {/* State Eligibility */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <MapPin className="h-4 w-4 text-blue-600" />
              <Label className="font-medium">Eligible States</Label>
              <Badge variant="secondary" className="ml-2">
                {settings.eligible_states.length} selected
              </Badge>
            </div>
            <div className="flex gap-2">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={selectAllStates}
              >
                Select All
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={clearAllStates}
              >
                Clear
              </Button>
            </div>
          </div>
          <p className="text-sm text-gray-500">
            Only orders from selected states will be auto-assigned to this processor
          </p>
          <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 lg:grid-cols-10 gap-2 max-h-48 overflow-y-auto p-2 border rounded-lg bg-white">
            {US_STATES.map((state) => (
              <button
                key={state.code}
                type="button"
                onClick={() => toggleState(state.code)}
                className={`
                  flex items-center justify-center gap-1 px-2 py-1.5 text-xs font-medium rounded-md
                  transition-colors cursor-pointer
                  ${settings.eligible_states.includes(state.code)
                    ? 'bg-blue-100 text-blue-700 border border-blue-300'
                    : 'bg-gray-100 text-gray-600 border border-gray-200 hover:bg-gray-200'
                  }
                `}
                title={state.name}
                data-testid={`state-${state.code}`}
              >
                {settings.eligible_states.includes(state.code) ? (
                  <CheckSquare className="h-3 w-3" />
                ) : (
                  <Square className="h-3 w-3" />
                )}
                {state.code}
              </button>
            ))}
          </div>
        </div>

        {/* Phase A.5 — Client Eligibility (empty = all clients) */}
        <div className="space-y-3" data-testid="eligible-clients-section">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Settings className="h-4 w-4 text-purple-600" />
              <Label className="font-medium">Eligible Clients</Label>
              <Badge variant="secondary" className="ml-2">
                {(settings.eligible_client_codes || []).length === 0
                  ? 'All clients'
                  : `${settings.eligible_client_codes.length} selected`}
              </Badge>
            </div>
            <Button type="button" variant="ghost" size="sm" onClick={clearClients}>
              Clear
            </Button>
          </div>
          <p className="text-sm text-gray-500">
            Leave empty to include all clients. Selecting one or more restricts auto-assignment to those clients only.
          </p>
          <Input
            type="text"
            placeholder="Filter clients..."
            value={clientFilter}
            onChange={(e) => setClientFilter(e.target.value)}
            className="w-full"
            data-testid="eligible-clients-filter"
          />
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 max-h-48 overflow-y-auto p-2 border rounded-lg bg-white">
            {clientsCatalog
              .filter(c => {
                const q = clientFilter.trim().toLowerCase();
                if (!q) return true;
                return (c.name || '').toLowerCase().includes(q)
                  || (c.code || '').toLowerCase().includes(q)
                  || (c.short_code || '').toLowerCase().includes(q);
              })
              .map((c) => {
                const selected = (settings.eligible_client_codes || []).includes(c.code);
                return (
                  <button
                    key={c.code}
                    type="button"
                    onClick={() => toggleClient(c.code)}
                    className={`flex items-center gap-1 px-2 py-1.5 text-xs font-medium rounded-md transition-colors cursor-pointer text-left ${
                      selected
                        ? 'bg-purple-100 text-purple-700 border border-purple-300'
                        : 'bg-gray-100 text-gray-600 border border-gray-200 hover:bg-gray-200'
                    }`}
                    title={c.code}
                    data-testid={`eligible-client-${c.code}`}
                  >
                    {selected ? <CheckSquare className="h-3 w-3 shrink-0" /> : <Square className="h-3 w-3 shrink-0" />}
                    <span className="truncate">{c.name}</span>
                  </button>
                );
              })}
            {clientsCatalog.length === 0 && (
              <div className="col-span-full text-xs text-gray-400 text-center py-2">
                No active clients found.
              </div>
            )}
          </div>
        </div>

        {/* Phase A.5 — Product Eligibility (empty = all products) */}
        <div className="space-y-3" data-testid="eligible-products-section">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Settings className="h-4 w-4 text-emerald-600" />
              <Label className="font-medium">Eligible Products</Label>
              <Badge variant="secondary" className="ml-2">
                {(settings.eligible_product_ids || []).length === 0
                  ? 'All products'
                  : `${settings.eligible_product_ids.length} selected`}
              </Badge>
            </div>
            <Button type="button" variant="ghost" size="sm" onClick={clearProducts}>
              Clear
            </Button>
          </div>
          <p className="text-sm text-gray-500">
            Leave empty to include all products. Selecting one or more restricts auto-assignment to orders containing those products.
          </p>
          <Input
            type="text"
            placeholder="Filter products..."
            value={productFilter}
            onChange={(e) => setProductFilter(e.target.value)}
            className="w-full"
            data-testid="eligible-products-filter"
          />
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 max-h-48 overflow-y-auto p-2 border rounded-lg bg-white">
            {productsCatalog
              .filter(p => {
                const q = productFilter.trim().toLowerCase();
                if (!q) return true;
                return (p.name || '').toLowerCase().includes(q)
                  || (p.category || '').toLowerCase().includes(q);
              })
              .map((p) => {
                const selected = (settings.eligible_product_ids || []).includes(p.id);
                return (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => toggleProduct(p.id)}
                    className={`flex items-center gap-1 px-2 py-1.5 text-xs font-medium rounded-md transition-colors cursor-pointer text-left ${
                      selected
                        ? 'bg-emerald-100 text-emerald-700 border border-emerald-300'
                        : 'bg-gray-100 text-gray-600 border border-gray-200 hover:bg-gray-200'
                    }`}
                    title={p.category ? `${p.name} (${p.category})` : p.name}
                    data-testid={`eligible-product-${p.id}`}
                  >
                    {selected ? <CheckSquare className="h-3 w-3 shrink-0" /> : <Square className="h-3 w-3 shrink-0" />}
                    <span className="truncate">{p.name}</span>
                  </button>
                );
              })}
            {productsCatalog.length === 0 && (
              <div className="col-span-full text-xs text-gray-400 text-center py-2">
                No active products found.
              </div>
            )}
          </div>
        </div>

        {/* Capacity Settings */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Daily Target */}
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Target className="h-4 w-4 text-green-600" />
              <Label htmlFor="daily_target">Daily Target</Label>
            </div>
            <Input
              id="daily_target"
              type="number"
              min="0"
              value={settings.daily_target}
              onChange={(e) => updateSetting('daily_target', e.target.value)}
              onBlur={(e) => updateSetting('daily_target', clampNumeric('daily_target', e.target.value))}
              className="w-full"
              data-testid="daily-target-input"
            />
            <p className="text-xs text-gray-500">
              0 = no daily limit (only WIP applies)
            </p>
          </div>

          {/* WIP Limit */}
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-orange-600" />
              <Label htmlFor="wip_limit">WIP Limit</Label>
            </div>
            <Input
              id="wip_limit"
              type="number"
              min="1"
              max="50"
              value={settings.wip_limit}
              onChange={(e) => updateSetting('wip_limit', e.target.value)}
              onBlur={(e) => updateSetting('wip_limit', clampNumeric('wip_limit', e.target.value))}
              className="w-full"
              data-testid="wip-limit-input"
            />
            <p className="text-xs text-gray-500">
              Maximum active orders at any time
            </p>
          </div>
        </div>

        {/* Stuck Protection */}
        <div className="space-y-3 p-4 bg-orange-50 rounded-lg border border-orange-200">
          <div className="flex items-center gap-2">
            <Clock className="h-4 w-4 text-orange-600" />
            <Label className="font-medium text-orange-800">Stuck Protection</Label>
          </div>
          <p className="text-sm text-orange-700">
            Pause auto-assignment if processor has too many stuck orders
          </p>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1">
              <Label htmlFor="stuck_count" className="text-sm">Threshold Count</Label>
              <Input
                id="stuck_count"
                type="number"
                min="1"
                max="50"
                value={settings.stuck_threshold_count}
                onChange={(e) => updateSetting('stuck_threshold_count', e.target.value)}
                onBlur={(e) => updateSetting('stuck_threshold_count', clampNumeric('stuck_threshold_count', e.target.value))}
                className="w-full"
                data-testid="stuck-count-input"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="stuck_hours" className="text-sm">Hours Threshold</Label>
              <Input
                id="stuck_hours"
                type="number"
                min="1"
                max="168"
                value={settings.stuck_threshold_hours}
                onChange={(e) => updateSetting('stuck_threshold_hours', e.target.value)}
                onBlur={(e) => updateSetting('stuck_threshold_hours', clampNumeric('stuck_threshold_hours', e.target.value))}
                className="w-full"
                data-testid="stuck-hours-input"
              />
            </div>
          </div>
          <p className="text-xs text-orange-600">
            If {settings.stuck_threshold_count}+ orders are stuck for {settings.stuck_threshold_hours}+ hours, 
            auto-assignment pauses for this processor
          </p>
        </div>

        {/* Lock Settings — the whole row is a click target (the bare
            switch knob was a small target inside the drawer and clicks
            on the label did nothing, which read as "toggle not working") */}
        <div
          className="flex items-center justify-between p-4 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors"
          onClick={() => updateSetting('assignment_lock_allowed', !settings.assignment_lock_allowed)}
          data-testid="lock-allowed-row"
        >
          <div>
            <Label className="font-medium cursor-pointer">Allow Assignment Lock</Label>
            <p className="text-sm text-gray-500">
              Allow Team Leaders to lock assignments (prevent auto-reassignment)
            </p>
          </div>
          {/* stopPropagation so a direct switch click doesn't also fire the
              row handler (which would double-toggle back to the start state) */}
          <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
            <span
              className={`text-sm font-semibold ${settings.assignment_lock_allowed ? 'text-green-600' : 'text-gray-400'}`}
              data-testid="lock-allowed-status-text"
            >
              {settings.assignment_lock_allowed ? 'ON' : 'OFF'}
            </span>
            <Switch
              checked={!!settings.assignment_lock_allowed}
              onCheckedChange={(checked) => updateSetting('assignment_lock_allowed', checked)}
              data-testid="lock-allowed-toggle"
            />
          </div>
        </div>

        {/* Save Button */}
        <div className="flex justify-end pt-4 border-t">
          <Button
            onClick={handleSave}
            disabled={saving || !hasChanges}
            className="min-w-32"
            data-testid="save-auto-assign-btn"
          >
            {saving ? (
              <>
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                Save Settings
              </>
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default AutoAssignmentSettings;
